/** Reportes de Inventario: consolidado (una fila por ítem) y desglosado (una fila por ingreso), mismos filtros y misma existencia. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtL, fmtNum, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PackageIcon, ChartLineIcon, WarningIcon, WarehouseIcon, DownloadIcon, StackIcon } from "@phosphor-icons/react";

const ND = "No disponible";
const v = (x) => (x === null || x === undefined || x === "" ? <span className="text-text-muted italic">{ND}</span> : x);
const VISTAS = {
  consolidado: { titulo: "Inventario consolidado", desc: "Una fila por ítem con su existencia total" },
  desglosado: { titulo: "Inventario desglosado", desc: "Una fila por ingreso: lote, batch, cantidades y demás datos" },
};

export default function InventoryReports() {
  const navigate = useNavigate();
  const [vista, setVista] = useState("consolidado");
  const [filtros, setFiltros] = useState({ q: "", categoria: "", bodega: "", solo_con_existencia: false, incluir_agotados: false });
  const [buscar, setBuscar] = useState("");
  const [data, setData] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/products/categories").catch(() => ({ data: [] })),
      api.get("/warehouses?activo=true&include_transito=false").catch(() => ({ data: [] })),
    ]).then(([c, w]) => { setCategorias(c.data || []); setWarehouses(w.data || []); });
  }, []);

  const params = () => ({ vista, ...Object.fromEntries(Object.entries(filtros).filter(([k, x]) => x !== "" && x !== false
    && !(k === "incluir_agotados" && vista !== "desglosado"))) });
  const load = async () => {
    setLoading(true);
    try { const { data: r } = await api.get("/reports/inventory/existencias", { params: params() }); setData(r); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vista, filtros]);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/reports/inventory/existencias/export-xlsx", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `inventario_${vista}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const t = data?.totales || {};
  const items = data?.items || [];
  const alertas = vista === "consolidado" ? items.filter((i) => i.alerta).length : null;
  return (
    <div className="space-y-5" data-testid="inventory-reports">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Kpi icon={PackageIcon} label="Ítems" value={t.productos || 0} accent="green" />
        <Kpi icon={StackIcon} label="Ingresos con saldo" value={t.ingresos || 0} />
        <Kpi icon={WarehouseIcon} label="Existencia física" value={fmtNum(t.existencia_fisica || 0)} />
        {data?.ver_costos
          ? <Kpi icon={ChartLineIcon} label="Valor de la existencia" value={fmtL(t.valor || 0)} accent="yellow" />
          : <Kpi icon={WarningIcon} label={vista === "consolidado" ? "Ítems en alerta" : "Disponible (catálogo)"}
              value={vista === "consolidado" ? alertas : (t.disponible === null ? "—" : fmtNum(t.disponible || 0))} accent="red" />}
      </div>

      <div className="bg-card border border-border/60 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="inv-toolbar">
        <div>
          <Lbl>Tipo de reporte</Lbl>
          <div className="flex border border-border rounded-md overflow-hidden">
            {Object.entries(VISTAS).map(([k, x]) => (
              <button key={k} onClick={() => setVista(k)} data-testid={`inv-vista-${k}`}
                className={`px-3 py-1.5 text-xs ${vista === k ? "bg-agro-green text-black font-semibold" : "text-text-secondary hover:bg-white/[0.04]"}`}>
                {x.titulo}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Lbl>Buscar ítem</Lbl>
          <form onSubmit={(e) => { e.preventDefault(); setFiltros({ ...filtros, q: buscar.trim() }); }}>
            <input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Código, nombre o SKU" data-testid="inv-q"
              className="bg-app border border-border rounded-md px-2 py-1.5 text-sm w-44" />
          </form>
        </div>
        <div>
          <Lbl>Categoría</Lbl>
          <select value={filtros.categoria} onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}
            data-testid="inv-cat" className="bg-app border border-border rounded-md px-2 py-1.5 text-sm w-40">
            <option value="">— Todas —</option>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Lbl>Bodega</Lbl>
          <select value={filtros.bodega} onChange={(e) => setFiltros({ ...filtros, bodega: e.target.value })}
            data-testid="inv-bod" className="bg-app border border-border rounded-md px-2 py-1.5 text-sm w-40">
            <option value="">— Todas —</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
          <input type="checkbox" checked={filtros.solo_con_existencia} data-testid="inv-solo-stock"
            onChange={(e) => setFiltros({ ...filtros, solo_con_existencia: e.target.checked })} className="accent-agro-green" />
          Solo con existencia
        </label>
        {vista === "desglosado" && (
          <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
            <input type="checkbox" checked={filtros.incluir_agotados} data-testid="inv-agotados"
              onChange={(e) => setFiltros({ ...filtros, incluir_agotados: e.target.checked })} className="accent-agro-green" />
            Incluir ingresos agotados
          </label>
        )}
        <div className="flex-1" />
        <button onClick={exportXlsx} data-testid="inv-export"
          className="bg-agro-yellow/15 text-agro-yellow border border-agro-yellow/30 hover:bg-agro-yellow/25 rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5">
          <DownloadIcon size={14} /> Exportar {VISTAS[vista].titulo.toLowerCase()} (Excel)
        </button>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex flex-wrap justify-between items-center gap-2">
          <div>
            <h3 className="font-heading text-base" data-testid="inv-titulo">{VISTAS[vista].titulo}</h3>
            <div className="text-[11px] text-text-muted">{VISTAS[vista].desc}. Existencia física = Σ saldos de los ingresos; disponible = existencia − reservado en proformas.</div>
          </div>
          <div className="text-xs text-text-muted text-right" data-testid="inv-totales">
            {items.length} {vista === "consolidado" ? "ítem(s)" : "fila(s)"} · Existencia: <strong className="text-text-primary">{fmtNum(t.existencia_fisica || 0)}</strong>
            {t.reservado_proformas !== null && t.reservado_proformas !== undefined && <> · Reservado: {fmtNum(t.reservado_proformas)} · Disponible: {fmtNum(t.disponible)}</>}
            {t.reservado_proformas === null && <div>Con filtro de bodega, reservas y disponible no aplican (son del ítem).</div>}
            {Math.abs(t.sin_ingreso_registrado || 0) > 0.0001 && <div className="text-agro-yellow">Incluye {fmtNum(t.sin_ingreso_registrado)} sin ingreso registrado</div>}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[600px]">
          {loading ? <div className="p-8 text-center text-text-muted">Cargando…</div>
            : vista === "consolidado" ? <TablaConsolidada items={items} navigate={navigate} costos={data?.ver_costos} />
            : <TablaDesglosada items={items} costos={data?.ver_costos} />}
        </div>
      </div>
    </div>
  );
}

function TablaConsolidada({ items, navigate, costos }) {
  return (
    <table className="w-full text-xs" data-testid="inv-tabla-consolidado">
      <thead className="bg-sidebar/60 sticky top-0">
        <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
          <th className="p-2 pl-4">Código</th><th className="p-2">Producto</th><th className="p-2">Categoría</th>
          <th className="p-2 text-right">Existencia física</th><th className="p-2 text-right">Ingresos</th>
          <th className="p-2 text-right">Sin ingreso reg.</th><th className="p-2 text-right">Reservado</th>
          <th className="p-2 text-right">Disponible</th><th className="p-2 text-right">En tránsito</th>
          {costos && <th className="p-2 text-right">Valor</th>}<th className="p-2 text-center">Alerta</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? <tr><td colSpan="11" className="p-8 text-center text-text-muted">Sin datos con estos filtros</td></tr>
          : items.map((p) => (
            <tr key={p.producto_id} className="border-t border-border/30 hover:bg-white/[0.02] cursor-pointer" onClick={() => navigate(`/productos/${p.producto_id}`)} data-testid={`inv-row-${p.codigo}`}>
              <td className="p-2 pl-4 font-mono text-agro-green">{p.codigo}</td>
              <td className="p-2">{p.nombre || p.sku} {p.sku && p.nombre && <span className="text-text-muted text-[10px]">· {p.sku}</span>}</td>
              <td className="p-2 text-text-secondary">{v(p.categoria)}</td>
              <td className="p-2 text-right font-medium">{fmtNum(p.existencia_fisica)} <span className="text-text-muted">{p.unidad}</span></td>
              <td className="p-2 text-right">{p.ingresos_con_saldo}</td>
              <td className={`p-2 text-right ${p.sin_ingreso_registrado ? "text-agro-yellow" : "text-text-muted"}`}>{fmtNum(p.sin_ingreso_registrado)}</td>
              <td className="p-2 text-right">{p.reservado_proformas === null ? "—" : fmtNum(p.reservado_proformas)}</td>
              <td className="p-2 text-right text-agro-green">{p.disponible === null ? "—" : fmtNum(p.disponible)}</td>
              <td className="p-2 text-right text-text-muted">{fmtNum(p.en_transito)}</td>
              {costos && <td className="p-2 text-right">{fmtL(p.valor_existencia)}</td>}
              <td className="p-2 text-center">
                {p.alerta === "sin" && <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 text-[10px]">Sin existencia</span>}
                {p.alerta === "bajo" && <span className="px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 text-[10px]">Bajo mín.</span>}
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

function TablaDesglosada({ items, costos }) {
  return (
    <table className="w-full text-xs" data-testid="inv-tabla-desglosado">
      <thead className="bg-sidebar/60 sticky top-0">
        <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
          <th className="p-2 pl-4">Código</th><th className="p-2">Producto</th><th className="p-2">N° ingreso / fecha</th>
          <th className="p-2">Bodega</th><th className="p-2">Batch</th><th className="p-2">Lote fab.</th><th className="p-2">Fechas</th>
          <th className="p-2">Proveedor</th><th className="p-2 text-right">Ingresado</th><th className="p-2 text-right">Salidas</th>
          <th className="p-2 text-right">Saldo</th>{costos && <th className="p-2 text-right">Costo / valor</th>}
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? <tr><td colSpan="12" className="p-8 text-center text-text-muted">Sin ingresos con estos filtros</td></tr>
          : items.map((it, i) => (
            <tr key={it.entry_id || `s${i}`} className={`border-t border-border/30 ${it.tipo_fila === "sin_ingreso" ? "bg-agro-yellow/5" : ""}`} data-testid={`inv-det-row-${i}`}>
              <td className="p-2 pl-4 font-mono text-agro-green">{it.codigo}</td>
              <td className="p-2">{it.nombre || it.sku}<div className="text-[10px] text-text-muted">{it.categoria}</div></td>
              <td className="p-2">{it.tipo_fila === "sin_ingreso" ? <span className="text-agro-yellow">Sin ingreso registrado</span> : <>{v(it.numero_ingreso)}<div className="text-text-muted">{v(it.fecha_ingreso)}</div></>}</td>
              <td className="p-2 font-mono">{v(it.bodega)}</td>
              <td className="p-2 font-mono">{v(it.batch)}</td>
              <td className="p-2 font-mono">{v(it.lote_fabricacion)}</td>
              <td className="p-2 text-[10px]">{it.es_semilla ? <>Test: {v(it.fecha_test)}</> : <>Fab: {v(it.fecha_fabricacion)}<div>Vence: {v(it.fecha_vencimiento)}</div></>}</td>
              <td className="p-2">{v(it.proveedor)}</td>
              <td className="p-2 text-right">{it.cantidad_ingresada === null ? v(null) : fmtNum(it.cantidad_ingresada)}</td>
              <td className="p-2 text-right">{it.salidas === null ? v(null) : fmtNum(it.salidas)}</td>
              <td className="p-2 text-right font-medium text-agro-green">{fmtNum(it.saldo)}</td>
              {costos && <td className="p-2 text-right">{it.costo_unitario === undefined ? v(null) : <>{fmtL(it.costo_unitario)}<div className="text-text-muted">{fmtL(it.valor_saldo)}</div></>}</td>}
            </tr>
          ))}
      </tbody>
    </table>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  const c = { green: "text-agro-green", yellow: "text-agro-yellow", red: "text-red-400" }[accent] || "text-text-primary";
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
        <Icon size={16} className={c} weight="duotone" />
      </div>
      <div className={`font-heading text-2xl mt-0.5 ${c}`}>{value}</div>
    </div>
  );
}

const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-muted mb-1 block">{children}</label>;
